import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const apiBase = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8787';

interface RefreshLogData {
  lastUpdated: string | null;
  recordCount: number;
}

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', timeZoneName: 'short',
    });
  } catch {
    return iso;
  }
}

export default function MethodologyPage() {
  const [refreshData, setRefreshData] = useState<RefreshLogData | null>(null);

  useEffect(() => {
    fetch(`${apiBase}/api/prices/refresh-log`)
      .then((r) => r.ok ? r.json() : Promise.reject())
      .then((d: RefreshLogData) => setRefreshData(d))
      .catch(() => { });
  }, []);

  return (
    <>
      <Helmet>
        <title>Pricing Methodology | AzureCalc.uk</title>
        <meta name="description" content="How AzureCalc.uk sources and verifies Azure prices — nightly API fetch, GBP rate mechanics, and what these estimates don't include." />
        <link rel="canonical" href="https://www.azure-calc.co.uk/methodology" />
      </Helmet>

      <div className="px-6 py-8 w-full max-w-3xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-1 text-[12px] mb-6 transition-colors" style={{ color: 'var(--text-tertiary)' }}>
          <span className="material-symbols-outlined text-[16px]">arrow_back</span>
          Back to calculators
        </Link>

        <h1 className="text-[28px] font-bold mb-2" style={{ color: 'var(--text-primary)' }}>Pricing Methodology</h1>
        <p className="text-[14px] mb-10 leading-relaxed" style={{ color: 'var(--text-tertiary)' }}>
          How the prices on this site are sourced, verified, and what they don't cover.
        </p>

        {/* Section 1: Where prices come from */}
        <section className="mb-10">
          <h2 className="text-[18px] font-bold mb-4 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
            <span className="material-symbols-outlined text-[#0078d4] text-[20px]">cloud_download</span>
            1. Where the prices come from
          </h2>
          <p className="text-[14px] leading-relaxed mb-4" style={{ color: 'var(--text-tertiary)' }}>
            Every night at 02:00 UTC I run a fetch against the Microsoft Azure Retail Prices API. The exact query is:
          </p>
          <pre className="border rounded p-4 text-[11px] font-mono overflow-x-auto mb-4 leading-relaxed whitespace-pre-wrap break-all" style={{ backgroundColor: 'var(--surface-container-low)', borderColor: 'var(--outline-variant)', color: 'var(--text-secondary)' }}>
            {`https://prices.azure.com/api/retail/prices
  ?api-version=2023-01-01-preview
  &$filter=armRegionName eq 'uksouth'
           and currencyCode eq 'GBP'`}
          </pre>
          <p className="text-[14px] leading-relaxed mb-4" style={{ color: 'var(--text-tertiary)' }}>
            The response is stored in a Cloudflare D1 database (SQLite). The calculators query this local
            cache — not the Azure API in real time — so every estimate reflects the prices from the last
            nightly refresh.
          </p>
          <div className="rounded p-4 text-[13px]" style={{ backgroundColor: 'var(--surface-container-low)', border: '1px solid rgba(0, 120, 212, 0.3)' }}>
            <p className="mb-1" style={{ color: 'var(--text-tertiary)' }}>Current cache status</p>
            {refreshData ? (
              <div className="flex flex-col gap-1">
                <p className="font-semibold" style={{ color: 'var(--text-secondary)' }}>
                  {refreshData.recordCount.toLocaleString()} prices cached
                  {refreshData.lastUpdated && <> · Last updated: {formatDateTime(refreshData.lastUpdated)}</>}
                </p>
              </div>
            ) : (
              <div className="h-4 rounded w-64 animate-pulse" style={{ backgroundColor: 'var(--surface-container)' }} />
            )}
          </div>
        </section>

        {/* Section 2: GBP mechanics */}
        <section className="mb-10">
          <h2 className="text-[18px] font-bold mb-4 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
            <span className="material-symbols-outlined text-[#0078d4] text-[20px]">currency_pound</span>
            2. How GBP pricing actually works
          </h2>
          <p className="text-[14px] leading-relaxed mb-4" style={{ color: 'var(--text-tertiary)' }}>
            The Azure Retail Prices API returns GBP natively — it is not a USD figure converted by
            this site. Microsoft determines the GBP price directly.
          </p>
          <p className="text-[14px] leading-relaxed mb-4" style={{ color: 'var(--text-tertiary)' }}>
            Specifically: Microsoft locks the GBP/USD exchange rate monthly, using London closing
            spot rates from the final business days of the prior month. This means your January invoice
            is priced using the rate Microsoft locked in December. The GBP rate can shift meaningfully
            between months if sterling moves — and it has.
          </p>
          <p className="text-[14px] leading-relaxed" style={{ color: 'var(--text-tertiary)' }}>
            This is why the prices shown here can differ slightly from what you see if you manually
            use the official Azure Pricing Calculator in USD and apply a current spot rate. The official
            GBP price is the authoritative figure; the USD equivalent is the approximation.
          </p>
        </section>

        {/* Section 3: Verification */}
        <section className="mb-10">
          <h2 className="text-[18px] font-bold mb-4 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
            <span className="material-symbols-outlined text-[#0078d4] text-[20px]">verified</span>
            3. How prices are verified
          </h2>
          <p className="text-[14px] leading-relaxed mb-4" style={{ color: 'var(--text-tertiary)' }}>
            The nightly refresh compares incoming prices against what was cached from the previous
            run. Any changed prices are logged with a timestamp. You can see recent movements on the{' '}
            <Link to="/history" className="text-[#0078d4] hover:underline">price history page</Link>.
          </p>
          <p className="text-[14px] leading-relaxed mb-4" style={{ color: 'var(--text-tertiary)' }}>
            I also spot-check prices against the official Azure pricing pages for the services I use
            most (Log Analytics, Blob Storage, Virtual Machines) whenever Microsoft announces a pricing
            update.
          </p>
          <p className="text-[14px] leading-relaxed" style={{ color: 'var(--text-tertiary)' }}>
            If you find a discrepancy, email{' '}
            <a href="mailto:gravitycontextdev@gmail.com" className="text-[#0078d4] hover:underline">
              gravitycontextdev@gmail.com
            </a>
            {' '}— I aim to investigate and correct within 24 hours.
          </p>
        </section>

        {/* Section 4: What these estimates don't include */}
        <section className="mb-10">
          <h2 className="text-[18px] font-bold mb-4 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
            <span className="material-symbols-outlined text-[#e8b000] text-[20px]">warning</span>
            4. What these estimates don't include
          </h2>
          <p className="text-[14px] leading-relaxed mb-4" style={{ color: 'var(--text-tertiary)' }}>
            These are retail / pay-as-you-go prices. The following are <strong style={{ color: 'var(--text-secondary)' }}>not</strong> reflected
            unless explicitly noted:
          </p>
          <ul className="text-[14px] leading-relaxed space-y-2 mb-4 list-disc pl-5" style={{ color: 'var(--text-tertiary)' }}>
            <li><strong style={{ color: 'var(--text-secondary)' }}>Enterprise Agreement discounts</strong> — EA customers typically get 15–30% off list</li>
            <li><strong style={{ color: 'var(--text-secondary)' }}>Reserved Instance pricing</strong> — 1-year saves ~40%, 3-year saves ~60% vs PAYG for VMs</li>
            <li><strong style={{ color: 'var(--text-secondary)' }}>Azure Hybrid Benefit</strong> — if you have on-prem Windows Server / SQL licences</li>
            <li><strong style={{ color: 'var(--text-secondary)' }}>Savings Plans</strong> — compute savings plans applied at subscription scope</li>
            <li><strong style={{ color: 'var(--text-secondary)' }}>Custom / CSP negotiated pricing</strong></li>
            <li><strong style={{ color: 'var(--text-secondary)' }}>Dev/Test pricing</strong> — Visual Studio subscribers get discounted rates on some services</li>
          </ul>
          <p className="text-[14px] leading-relaxed" style={{ color: 'var(--text-tertiary)' }}>
            Always verify through the{' '}
            <a
              href="https://azure.microsoft.com/pricing/calculator"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#0078d4] hover:underline"
            >
              official Azure Pricing Calculator ↗
            </a>{' '}
            before committing to a spending plan.
          </p>
        </section>

        {/* Section 5: Data freshness */}
        <section className="mb-10">
          <h2 className="text-[18px] font-bold mb-4 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
            <span className="material-symbols-outlined text-[#0078d4] text-[20px]">schedule</span>
            5. Data freshness
          </h2>
          <p className="text-[14px] leading-relaxed mb-4" style={{ color: 'var(--text-tertiary)' }}>
            The price cache refreshes every night at 02:00 UTC. If the nightly job fails (Azure API
            timeout, etc.), the previous night's prices remain live and a stale warning appears on
            the calculators. Failures are rare — the Azure Retail Prices API has been reliable in my
            experience — but they happen.
          </p>
          <p className="text-[14px] leading-relaxed" style={{ color: 'var(--text-tertiary)' }}>
            If prices look wrong or outdated, check the{' '}
            <Link to="/history" className="text-[#0078d4] hover:underline">price history page</Link>{' '}
            for the last successful refresh timestamp, or email me directly.
          </p>
        </section>

        {/* Section 6: Rounding and precision */}
        <section className="mb-10">
          <h2 className="text-[18px] font-bold mb-4 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
            <span className="material-symbols-outlined text-[#0078d4] text-[20px]">calculate</span>
            6. Rounding, precision, and display conventions
          </h2>
          <p className="text-[14px] leading-relaxed mb-4" style={{ color: 'var(--text-tertiary)' }}>
            Azure prices are stored in the API to many decimal places (e.g., £0.000004567/GB). For
            readability, the calculators round results to two decimal places in the totals view, and
            show full precision in formula breakdowns where relevant. This means a calculated total
            might be off by a penny, but the driving logic is transparent.
          </p>
          <p className="text-[14px] leading-relaxed mb-4" style={{ color: 'var(--text-tertiary)' }}>
            When you save or share an estimate via URL, the inputs are encoded but precision may vary
            depending on the browser and network. For critical cost decisions, regenerate the estimate
            on a fresh load to confirm.
          </p>
          <p className="text-[14px] leading-relaxed" style={{ color: 'var(--text-tertiary)' }}>
            All monthly estimates assume 730 hours in a month (365 days ÷ 12). Actual months are 28–31
            days, so actual costs may vary slightly. For precision, default to calendar-month actuals
            from your Azure invoice.
          </p>
        </section>

        {/* Section 7: Region and service scope */}
        <section className="mb-10">
          <h2 className="text-[18px] font-bold mb-4 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
            <span className="material-symbols-outlined text-[#0078d4] text-[20px]">public</span>
            7. Region scope: UK South only
          </h2>
          <p className="text-[14px] leading-relaxed mb-4" style={{ color: 'var(--text-tertiary)' }}>
            AzureCalc.uk is scoped to UK South exclusively. Prices for other regions (North Europe,
            West Europe, etc.) are not included. UK South is used as the example region, but pricing
            for other regions can be looked up via the official Azure Pricing Calculator.
          </p>
          <p className="text-[14px] leading-relaxed mb-4" style={{ color: 'var(--text-tertiary)' }}>
            Why UK South specifically? It's where most UK enterprise workloads land, and it's a good
            baseline for European pricing. If your workload runs across multiple regions, multi-region
            costs (peering, replication, egress between regions) are shown in the Bandwidth and
            Networking calculators.
          </p>
          <p className="text-[14px] leading-relaxed" style={{ color: 'var(--text-tertiary)' }}>
            Services may not be available in UK South; if a service is unavailable, the calculator
            will display a note. Check the official{' '}
            <a
              href="https://azure.microsoft.com/global-infrastructure/services"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#0078d4] hover:underline"
            >
              Azure Products by Region
            </a>{' '}
            to find an alternative.
          </p>
        </section>

        {/* Section 8: Known limitations */}
        <section className="mb-10">
          <h2 className="text-[18px] font-bold mb-4 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
            <span className="material-symbols-outlined text-[#e8b000] text-[20px]">flag</span>
            8. Known limitations and edge cases
          </h2>
          <ul className="text-[14px] leading-relaxed space-y-3 mb-4 list-disc pl-5" style={{ color: 'var(--text-tertiary)' }}>
            <li><strong style={{ color: 'var(--text-secondary)' }}>Spot instances and variable capacity:</strong> Spot VM pricing
              fluctuates based on capacity and demand. The calculator shows current list price; actual costs may be lower or higher
              on any given day. Budget using sustained list price, not spot.</li>
            <li><strong style={{ color: 'var(--text-secondary)' }}>Throttling and burst pricing:</strong> Some services (e.g.,
              Cosmos DB, Storage) have burst capacity and throttling penalties that charge extra. The calculator shows base rate
              only, not penalty surges.</li>
            <li><strong style={{ color: 'var(--text-secondary)' }}>Bandwidth minimum billing:</strong> Some services (e.g., Archive
              storage) bill a minimum quantity if you retrieve less than that minimum. These are baked into the calculator. Check
              the service-specific guide for details.</li>
            <li><strong style={{ color: 'var(--text-secondary)' }}>Free tier and free allowances:</strong> Many services have free
              monthly grants (e.g., Functions: 1M executions free). These are factored into the calculators. Verify the grant still
              applies to your subscription tier.</li>
            <li><strong style={{ color: 'var(--text-secondary)' }}>Multi-tenant and dedicated capacity:</strong> Some services offer
              both shared (multi-tenant) and dedicated (single-tenant) options with different pricing. The calculator shows shared
              pricing by default; dedicated pricing is not included.</li>
          </ul>
        </section>

        {/* Section 9: Next steps */}
        <section className="mb-10">
          <h2 className="text-[18px] font-bold mb-4 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
            <span className="material-symbols-outlined text-[#0078d4] text-[20px]">arrow_forward</span>
            9. How to use these estimates
          </h2>
          <p className="text-[14px] leading-relaxed mb-4" style={{ color: 'var(--text-tertiary)' }}>
            The estimates on AzureCalc.uk are a starting point for team discussions and capacity planning,
            not procurement quotes. Use them to:
          </p>
          <ul className="text-[14px] leading-relaxed space-y-2 mb-4 list-disc pl-5" style={{ color: 'var(--text-tertiary)' }}>
            <li>Compare architectures (e.g., does VMs or Functions make more sense for this workload?)</li>
            <li>Brief non-technical stakeholders on order-of-magnitude costs in GBP</li>
            <li>Spot-check whether your invoice seems reasonable (£10/month Function app, or £100/month?)</li>
            <li>Alert to pricing movements before you commit to a service</li>
          </ul>
          <p className="text-[14px] leading-relaxed" style={{ color: 'var(--text-tertiary)' }}>
            For formal quotes or enterprise agreements, always use the official{' '}
            <a
              href="https://azure.microsoft.com/pricing/calculator"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#0078d4] hover:underline"
            >
              Azure Pricing Calculator
            </a>{' '}
            or contact sales.
          </p>
        </section>

        {/* Section 10: Price sources by calculator */}
        <section className="mb-10">
          <h2 className="text-[18px] font-bold mb-4 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
            <span className="material-symbols-outlined text-[#0078d4] text-[20px]">table_chart</span>
            10. Price sources by calculator
          </h2>
          <p className="text-[14px] leading-relaxed mb-5" style={{ color: 'var(--text-tertiary)' }}>
            Each calculator reads prices from D1 using the service name stored by the nightly price refresh.
            The table below is the authoritative reference for independent audits — it documents the exact
            service name queried, region filter applied, and which prices are auto-refreshed vs hardcoded.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-[12px] border-collapse">
              <thead>
                <tr style={{ backgroundColor: 'var(--surface-container-low)', borderBottom: '2px solid var(--outline-variant)' }}>
                  <th className="text-left p-2 font-semibold" style={{ color: 'var(--text-secondary)' }}>Calculator</th>
                  <th className="text-left p-2 font-semibold" style={{ color: 'var(--text-secondary)' }}>D1 service_name(s)</th>
                  <th className="text-left p-2 font-semibold" style={{ color: 'var(--text-secondary)' }}>Region</th>
                  <th className="text-left p-2 font-semibold" style={{ color: 'var(--text-secondary)' }}>Refreshed</th>
                  <th className="text-left p-2 font-semibold" style={{ color: 'var(--text-secondary)' }}>Hardcoded</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { calc: 'Blob Storage', service: 'Storage', region: 'uksouth', refreshed: '✅ Nightly', hardcoded: 'None' },
                  { calc: 'Logic Apps', service: 'Logic Apps', region: 'uksouth', refreshed: '✅ Nightly', hardcoded: 'None' },
                  { calc: 'Azure Functions', service: 'Functions', region: 'uksouth', refreshed: '✅ Nightly', hardcoded: 'None' },
                  { calc: 'App Service', service: 'Azure App Service', region: 'uksouth', refreshed: '✅ Nightly', hardcoded: 'None' },
                  { calc: 'Bandwidth', service: 'Bandwidth', region: 'uksouth', refreshed: '✅ Nightly', hardcoded: 'None' },
                  { calc: 'Log Analytics', service: 'Log Analytics, Azure Monitor', region: 'uksouth', refreshed: '✅ Nightly', hardcoded: 'None' },
                  { calc: 'Sentinel', service: 'Sentinel', region: 'uksouth', refreshed: '✅ Nightly', hardcoded: 'None' },
                  { calc: 'Virtual Machines', service: 'Virtual Machines', region: 'uksouth', refreshed: '✅ Filtered (5 SKUs)', hardcoded: 'None' },
                  { calc: 'Networking', service: 'Virtual Network, Azure DDOS Protection', region: 'uksouth', refreshed: '✅ Nightly', hardcoded: 'NAT Gateway £0.0449/hr' },
                  { calc: 'Data Factory', service: 'Azure Data Factory', region: 'uksouth', refreshed: '✅ Nightly', hardcoded: 'Data Flow GP/MO/CO rates' },
                  { calc: 'Virtual Desktop', service: 'Azure Files, Virtual Machines', region: 'uksouth', refreshed: '✅ Nightly', hardcoded: 'External user access fees (USD)' },
                  { calc: 'Front Door', service: 'Azure Front Door, Azure Front Door Service', region: 'Zone 1', refreshed: '✅ Nightly', hardcoded: 'None' },
                  { calc: 'SQL Database', service: 'SQL Database', region: 'uksouth', refreshed: '✅ Nightly', hardcoded: 'None' },
                  { calc: 'Azure Firewall', service: 'Azure Firewall', region: 'uksouth', refreshed: '✅ Nightly (fallback active)', hardcoded: 'Fallback rates if D1 empty' },
                  { calc: 'Azure OpenAI', service: 'Foundry Models', region: 'uksouth', refreshed: '✅ Nightly (Sprint 10)', hardcoded: 'Model rates (API returns £0 for uksouth)' },
                  { calc: 'Managed Disks', service: 'Azure Managed Disks (via productName)', region: 'uksouth', refreshed: '✅ Nightly (Sprint 11)', hardcoded: 'Pv2/Ultra IOPS+throughput (API returns £0)' },
                ].map((row, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--outline-variant)', backgroundColor: i % 2 === 0 ? 'transparent' : 'var(--surface-container-low)' }}>
                    <td className="p-2 font-medium" style={{ color: 'var(--text-secondary)' }}>{row.calc}</td>
                    <td className="p-2 font-mono text-[11px]" style={{ color: 'var(--text-tertiary)' }}>{row.service}</td>
                    <td className="p-2 font-mono text-[11px]" style={{ color: 'var(--text-tertiary)' }}>{row.region}</td>
                    <td className="p-2 text-[11px]" style={{ color: 'var(--text-tertiary)' }}>{row.refreshed}</td>
                    <td className="p-2 text-[11px]" style={{ color: row.hardcoded === 'None' ? 'var(--text-tertiary)' : '#e8b000' }}>{row.hardcoded}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Section 11: Hardcoded prices */}
        <section className="mb-10">
          <h2 className="text-[18px] font-bold mb-4 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
            <span className="material-symbols-outlined text-[#e8b000] text-[20px]">edit_note</span>
            11. Hardcoded prices — manual verification required
          </h2>
          <p className="text-[14px] leading-relaxed mb-4" style={{ color: 'var(--text-tertiary)' }}>
            These prices are <strong style={{ color: 'var(--text-secondary)' }}>not returned by the Azure Retail Prices API</strong> and are
            hardcoded in the worker. They must be manually verified before each sprint release.
          </p>
          <pre className="border rounded p-4 text-[11px] font-mono overflow-x-auto leading-relaxed" style={{ backgroundColor: 'var(--surface-container-low)', borderColor: 'var(--outline-variant)', color: 'var(--text-secondary)' }}>
{`NAT Gateway hourly      £0.0449/hr        Verified Apr 2026
  Source: azure.microsoft.com/en-gb/pricing/details/azure-nat-gateway/

ADF Data Flow GP        £0.216/vCore-hr   Verified Apr 2026
ADF Data Flow MO        £0.270/vCore-hr   Verified Apr 2026
ADF Data Flow CO        £0.172/vCore-hr   Verified Apr 2026
  Source: azure.microsoft.com/en-gb/pricing/details/data-factory/

AVD external (apps)     $5.50/user/month  Verified Apr 2026
AVD external (full)     $10.00/user/month Verified Apr 2026
  Source: azure.microsoft.com/en-gb/pricing/details/virtual-desktop/
  Note: USD-denominated, converted at approximate rate for GBP display

OpenAI gpt-4o input     £0.0019/1K tokens Verified Apr 2026
OpenAI gpt-4o output    £0.0075/1K tokens Verified Apr 2026
OpenAI gpt-4o-mini in   £0.0001/1K tokens Verified Apr 2026
OpenAI gpt-4o-mini out  £0.0005/1K tokens Verified Apr 2026
OpenAI o3-mini input    £0.0008/1K tokens Verified Apr 2026
OpenAI o3-mini output   £0.0033/1K tokens Verified Apr 2026
OpenAI gpt-4 input      £0.0226/1K tokens Verified Apr 2026
OpenAI gpt-4 output     £0.0451/1K tokens Verified Apr 2026
  Source: Azure Retail Prices API direct (Foundry Models service, GBP endpoint)
  Note: Changes frequently — verify every sprint

Premium SSD v2 IOPS     £0.00657/IOPS/month   Verified Apr 2026
Premium SSD v2 throughput £0.2197/MBps/month  Verified Apr 2026
Ultra Disk IOPS         £0.0550/IOPS/month    Verified Apr 2026
  Source: Microsoft Azure documentation (API returns £0 for these meters)
  Note: Re-verify each sprint — API may start returning these`}
          </pre>
        </section>

        {/* Section 12: D1 verification queries */}
        <section className="mb-10">
          <h2 className="text-[18px] font-bold mb-2 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
            <span className="material-symbols-outlined text-[#0078d4] text-[20px]">code</span>
            12. D1 verification queries
          </h2>
          <p className="text-[14px] leading-relaxed mb-4" style={{ color: 'var(--text-tertiary)' }}>
            Run these queries against the D1 database to verify stored prices match the live API.
            Services marked ⚠️ require an additional source check beyond the D1 query alone.
          </p>
          <pre className="border rounded p-4 text-[11px] font-mono overflow-x-auto leading-relaxed whitespace-pre" style={{ backgroundColor: 'var(--surface-container-low)', borderColor: 'var(--outline-variant)', color: 'var(--text-secondary)' }}>
{`-- Blob Storage: Hot LRS per GB/month (UK South)
SELECT sku_name, retail_price, unit_of_measure
FROM azure_prices
WHERE service_name = 'Storage'
  AND sku_name = 'Hot LRS Data Stored'
  AND arm_region_name = 'uksouth';

-- Log Analytics: PAYG ingestion per GB (UK South)
SELECT sku_name, retail_price, unit_of_measure
FROM azure_prices
WHERE service_name = 'Log Analytics'
  AND sku_name = 'Analytics Logs'
  AND unit_of_measure = '1 GB'
  AND arm_region_name = 'uksouth';

-- Sentinel: PAYG per GB (UK South)
SELECT sku_name, retail_price, unit_of_measure
FROM azure_prices
WHERE service_name = 'Sentinel'
  AND sku_name = 'Security Insights'
  AND unit_of_measure = '1 GB'
  AND arm_region_name = 'uksouth';

-- Virtual Machines: D2s v3 Linux (UK South)
SELECT sku_name, retail_price, unit_of_measure
FROM azure_prices
WHERE service_name = 'Virtual Machines'
  AND sku_name = 'D2s v3'
  AND arm_region_name = 'uksouth';

-- Networking: VNet peering per GB (UK South)
SELECT sku_name, retail_price, unit_of_measure
FROM azure_prices
WHERE service_name = 'Virtual Network'
  AND arm_region_name = 'uksouth'
ORDER BY sku_name;

-- ⚠️ Networking: NAT Gateway — NOT IN D1
-- Hardcoded at £0.0449/hr. Verify manually:
-- https://azure.microsoft.com/en-gb/pricing/details/azure-nat-gateway/

-- Networking: Public IP (UK South)
SELECT sku_name, retail_price, unit_of_measure
FROM azure_prices
WHERE service_name = 'Virtual Network'
  AND sku_name LIKE '%Public IP%'
  AND arm_region_name = 'uksouth';

-- Data Factory: Copy Activity DIU-hour (UK South)
SELECT sku_name, retail_price, unit_of_measure
FROM azure_prices
WHERE service_name = 'Azure Data Factory'
  AND sku_name LIKE '%Data Movement%'
  AND arm_region_name = 'uksouth';

-- ⚠️ Data Factory: Data Flow rates — NOT IN D1
-- GP £0.216, MO £0.270, CO £0.172/vCore-hr
-- Verify: https://azure.microsoft.com/en-gb/pricing/details/data-factory/

-- Azure Files: Premium provisioned storage (Virtual Desktop FSLogix)
SELECT sku_name, retail_price, unit_of_measure
FROM azure_prices
WHERE service_name = 'Storage'
  AND sku_name LIKE '%Premium Files%'
  AND arm_region_name = 'uksouth'
ORDER BY sku_name;

-- ⚠️ Front Door: Zone 1 (NOT uksouth — zone-based pricing)
SELECT sku_name, retail_price, unit_of_measure
FROM azure_prices
WHERE service_name = 'Azure Front Door'
  AND arm_region_name = 'Zone 1'
ORDER BY sku_name;

-- SQL Database: General Purpose vCore (UK South)
SELECT sku_name, retail_price, unit_of_measure
FROM azure_prices
WHERE service_name = 'SQL Database'
  AND sku_name LIKE '%General Purpose%'
  AND arm_region_name = 'uksouth'
ORDER BY sku_name LIMIT 10;

-- Azure Firewall: Standard deployment (UK South)
SELECT sku_name, retail_price, unit_of_measure
FROM azure_prices
WHERE service_name = 'Azure Firewall'
  AND arm_region_name = 'uksouth'
ORDER BY sku_name;

-- ⚠️ Azure OpenAI: GPT-4o (Foundry Models — changes frequently)
-- If 0 rows returned, hardcoded values are in use — verify via API directly.
SELECT sku_name, retail_price, unit_of_measure
FROM azure_prices
WHERE service_name = 'Foundry Models'
  AND sku_name LIKE '%gpt-4o%'
ORDER BY sku_name;

-- Managed Disks: Standard HDD S10 (UK South)
SELECT sku_name, retail_price, unit_of_measure
FROM azure_prices
WHERE service_name = 'Azure Managed Disks'
  AND sku_name LIKE '%S10%'
  AND arm_region_name = 'uksouth';`}
          </pre>
        </section>

        {/* Section 13: Formula-level test cases */}
        <section className="mb-10">
          <h2 className="text-[18px] font-bold mb-4 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
            <span className="material-symbols-outlined text-[#0078d4] text-[20px]">science</span>
            13. Formula-level test cases
          </h2>
          <p className="text-[14px] leading-relaxed mb-4" style={{ color: 'var(--text-tertiary)' }}>
            For calculators where rate verification alone is insufficient, these expected outputs verify
            the calculation logic. These are the test cases for the independent accuracy audit after Sprint 14.
          </p>

          <h3 className="text-[15px] font-semibold mb-2 mt-6" style={{ color: 'var(--text-secondary)' }}>Log Analytics</h3>
          <pre className="border rounded p-4 text-[11px] font-mono overflow-x-auto leading-relaxed whitespace-pre" style={{ backgroundColor: 'var(--surface-container-low)', borderColor: 'var(--outline-variant)', color: 'var(--text-secondary)' }}>
{`Test 1 — PAYG, no Sentinel, 30-day retention
  Input:  15 GB/day, PAYG tier, 30-day retention, no Sentinel
  Expected: 15 × 30 × £2.167 = £975.15/month ingestion
            Retention: £0 (≤31 days free)
            Total: ~£975/month

Test 2 — Commitment tier break-even
  Input:  95 GB/day, 100 GB/day commitment tier
  Expected: 100 × 30 × £1.937 = £5,811/month commitment
            vs PAYG: 95 × 30 × £2.167 = £6,175.65/month
            Saving: ~£365/month — commitment tier recommended

Test 3 — Sentinel overlay
  Input:  10 GB/day, PAYG, Sentinel enabled
  Expected: 10 × 30 × (£2.167 + £2.297) = £1,339.20/month`}
          </pre>

          <h3 className="text-[15px] font-semibold mb-2 mt-6" style={{ color: 'var(--text-secondary)' }}>SQL Database</h3>
          <pre className="border rounded p-4 text-[11px] font-mono overflow-x-auto leading-relaxed whitespace-pre" style={{ backgroundColor: 'var(--surface-container-low)', borderColor: 'var(--outline-variant)', color: 'var(--text-secondary)' }}>
{`Test 1 — DTU Standard S3
  Input:  Standard S3 (100 DTUs), single database
  Expected: flat monthly rate from D1 for S3 uksouth
            No transaction charges (DTU bundles IO)

Test 2 — vCore GP with AHB + 1-year RI
  Input:  GP, 4 vCores, provisioned, AHB enabled, 1-year RI
  Expected: base compute × (1 - AHB discount) × (1 - RI 1yr ~33%)
            Storage charged separately per GB
            AHB removes Windows licence component (~30% of compute)

Test 3 — Savings Plan (March 2026)
  Input:  GP, 4 vCores, provisioned, Savings Plan 1-year
  Expected: up to 35% saving vs PAYG compute
            Note: Savings Plan and RI are mutually exclusive`}
          </pre>

          <h3 className="text-[15px] font-semibold mb-2 mt-6" style={{ color: 'var(--text-secondary)' }}>Azure OpenAI</h3>
          <pre className="border rounded p-4 text-[11px] font-mono overflow-x-auto leading-relaxed whitespace-pre" style={{ backgroundColor: 'var(--surface-container-low)', borderColor: 'var(--outline-variant)', color: 'var(--text-secondary)' }}>
{`Test 1 — Standard mode, moderate usage
  Input:  GPT-4o, 1M input tokens/day, 200K output tokens/day
  Expected daily: (1000 × £0.0019) + (200 × £0.0075) = £1.90 + £1.50 = £3.40
  Expected monthly: £3.40 × 30 = £102/month

Test 2 — PTU break-even
  Input:  100 PTUs, 1-month commitment, 22 active days/month
  PTU monthly = 100 × £0.7524 × 730 × (1 - 0.05) = £521.79/month
  Break-even = £521.79 ÷ £3.40 = ~153 days (recommendation: Standard)

Test 3 — Image requests in break-even
  Input:  GPT-4o, 500K tokens/day, 1,000 image requests/day
  Standard daily = (500 × £0.0019) + 0 + (1000 × £0.0577) = £0.95 + £57.70 = £58.65
  Verify: break-even denominator includes image cost`}
          </pre>

          <h3 className="text-[15px] font-semibold mb-2 mt-6" style={{ color: 'var(--text-secondary)' }}>Virtual Desktop</h3>
          <pre className="border rounded p-4 text-[11px] font-mono overflow-x-auto leading-relaxed whitespace-pre" style={{ backgroundColor: 'var(--surface-container-low)', borderColor: 'var(--outline-variant)', color: 'var(--text-secondary)' }}>
{`Test 1 — 50 internal users, eligible M365
  Input:  50 internal users, M365 eligible, 5× D2s v3 session hosts,
          FSLogix Premium 500GB
  Expected: VM cost = 5 × D2s_v3_rate × 730
            Storage: Premium Files rate × 500GB
            Licensing: £0 (M365 eligible)
            Per-user = total ÷ 50

Test 2 — FSLogix Standard vs Premium comparison
  Input:  500 GB provisioned, 1M transactions/month
  Expected: Standard = base_fee + (1000 × transaction_rate)
            Premium = base_fee_premium (no transactions)
            Calculator flags if Standard total > Premium total`}
          </pre>
        </section>

        <div className="pt-6 text-[12px]" style={{ borderTop: '1px solid var(--surface-container-high)', color: 'var(--text-tertiary)' }}>
          AzureCalc.uk is independently operated. Not affiliated with Microsoft Corporation.
          Pricing data sourced from the{' '}
          <a
            href="https://learn.microsoft.com/en-us/rest/api/cost-management/retail-prices/azure-retail-prices"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:opacity-70"
          >
            Microsoft Azure Retail Prices API
          </a>
          .
        </div>
      </div>
    </>
  );
}
